import {
  Box,
  Button,
  createTheme,
  Pagination,
  Paper,
  Skeleton,
  TablePagination,
  Theme,
  ThemeProvider,
  Typography,
} from '@mui/material';
import { TablePaginationActionsProps } from '@mui/material/TablePagination/TablePaginationActions';
import makeStyles from '@mui/styles/makeStyles';
import {
  DataGridProps,
  GridColDef,
  GridRowData,
  GridRowId,
  GridRowModel,
  GridSortModel,
  GridValidRowModel,
  GridValueFormatterParams,
  GridValueGetterParams,
} from '@mui/x-data-grid';
// import { useDemoData } from '@material-ui/x-grid-data-generator';
import get from 'lodash/get';
// import { useDispatch } from 'react-redux';
// import GridLoader from 'react-spinners/GridLoader';
// import { updateDisplayLargeTableMsg } from '../../../../store/largeTableSlice';
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_PaginationState,
  MRT_RowSelectionState,
  MRT_Updater,
} from 'material-react-table';
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Data } from 'react-csv/components/CommonPropTypes';
import { useRecoilValue } from 'recoil';

import { themeModeAtom, themeModeEnum } from 'src/recoil/atoms/themeModeAtom';

import type { SortingState } from '@tanstack/table-core/build/lib/features/RowSorting';
import { useFeatureFlag } from 'src/app/hooks/useFeatureFlag';
import DragDataTable from './DragDataTable';
import DragDataTable2 from './DragDataTable2';
import SlideDataTable from './SlideDataTable';

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    backgroundColor:
      theme.palette.mode === 'dark' ? '#121c21 !important' : '#fff',
    // width: 'auto', // allow table to stretch full width
    flexGrow: 1, // allow table to stretch full width
    '& > div': {
      backgroundColor:
        theme.palette.mode === 'dark' ? '#121c21 !important' : '#fff',
      height: 'auto !important', // override zero height from DataGrid
      width: 'auto !important', // override zero width from DataGrid
    },
    '& .MuiDataGrid-root': {
      overflow: 'auto', // set table to be responsive
      fontSize: '0.9rem',
      fontFamily: 'Open Sans',
    },
    '& .MuiBox-root': {
      backgroundColor:
        theme.palette.mode === 'dark' ? '#121c21 !important' : '#fff',
    },
    '& .MuiTableCell-root': {
      backgroundColor:
        theme.palette.mode === 'dark' ? '#121c21 !important' : '#fff',
    },
    '& > .tableFullScreenEsc': {
      inset: 'initial !important',
      height: 'auto !important',
      margin: 'auto !important',
      maxHeight: 'auto !important',
      maxWidth: 'auto !important',
      padding: '0px !important',
      position: 'relative !important',
      width: 'auto !important',
      zIndex: 'auto !important',
      '.MuiBox-root.jwgEMu': {
        position: 'relative !important',
      },
    },
  },
  skeleton: {
    backgroundColor: theme?.palette?.mode === 'dark' ? 'grey' : 'lightgrey',
    height: '6em',
    margin: 10,
  },
  hideLastPageButton: {
    '& .MuiPagination-ul > li:nth-last-child(2)': {
      display: 'none',
    },
  },
  pagination: {
    '& .MuiTablePagination-toolbar': {
      flexFlow: 'wrap',
      alignItems: 'baseline',
      paddingLeft: theme?.spacing(2),
    },
    '& .MuiTablePagination-toolbar > p:nth-child(2)': {
      display: 'flex',
      flexGrow: 1,
    },
  },
  paginationTotalRows: {
    flexGrow: 1,
  },
  tableLayoutFixed: {
    tableLayout: 'fixed',
  },
  tableTopBanner: {
    position: 'absolute',
    top: 0,
    left: 0,
    display: 'flex',
    padding: '6.5px 13px',
    flexDirection: 'row',
    alignItems: 'center',
    color: 'rgb(102, 59, 20)',
    backgroundColor: 'rgb(255, 244, 234)',
    width: '100%',
  },
}));

const TableSkeleton = (tabTable) => {
  const classes = useStyles();
  return tabTable ? (
    <Paper elevation={0}>
      <Skeleton
        className={classes.skeleton}
        variant="rectangular"
        height={52}
      />
      <Skeleton
        className={classes.skeleton}
        variant="rectangular"
        height={52}
      />
      <Skeleton
        className={classes.skeleton}
        variant="rectangular"
        height={52}
      />
      <Skeleton
        className={classes.skeleton}
        variant="rectangular"
        height={52}
      />
      <Skeleton
        className={classes.skeleton}
        variant="rectangular"
        height={52}
      />
    </Paper>
  ) : (
    <Paper elevation={0}>
      <Skeleton
        className={classes.skeleton}
        variant="rectangular"
        height={50}
      />
      <Skeleton
        className={classes.skeleton}
        variant="rectangular"
        height={50}
      />
      <Skeleton
        className={classes.skeleton}
        variant="rectangular"
        height={50}
      />
      <Skeleton
        className={classes.skeleton}
        variant="rectangular"
        height={50}
      />
      <Skeleton
        className={classes.skeleton}
        variant="rectangular"
        height={50}
      />
      <Skeleton
        className={classes.skeleton}
        variant="rectangular"
        height={50}
      />
      <Skeleton
        className={classes.skeleton}
        variant="rectangular"
        height={50}
      />
      <Skeleton
        className={classes.skeleton}
        variant="rectangular"
        height={50}
      />
      <Skeleton
        className={classes.skeleton}
        variant="rectangular"
        height={50}
      />
      <Skeleton
        className={classes.skeleton}
        variant="rectangular"
        height={50}
      />
      <Box display="flex" margin={1} justifyContent="flex-end">
        <Skeleton variant="circular" width={40} height={40} />
        <Skeleton variant="circular" width={40} height={40} />
        <Skeleton variant="circular" width={40} height={40} />
      </Box>
    </Paper>
  );
};

export interface IToolbarComponent {
  /** Search string to be displayed in TextField */
  search: string;
  /** Callback to set new search string */
  setSearch: (value: string) => void;
  /** CSVLink data */
  csvData?: Data;
  /** Data is loading */
  isLoading?: boolean;
}
export interface IColDef<R extends GridValidRowModel = any, V = any, F = V>
  extends GridColDef<R, V, F> {
  /** Search should exclude this column */
  disableSearch?: boolean;
  /** Disable row selection on click */
  disableRowSelection?: boolean;
  /** Disable export */
  disableExport?: boolean;
  /** Width property for DragDataTable2.tsx */
  dragTableWidth?: string;
  /** Enable sorting for this column */
  enableSorting?: boolean;
  Header?: any;
  /**
   * Function that allows to apply a formatter before rendering its value.
   * @template V, F
   * @param {GridValueFormatterParams<V>} params Object containing parameters for the formatter.
   * @returns {F} The formatted value.
   */
  valueFormatter?: (
    params: GridValueFormatterParams<V> & GridValueGetterParams['row']
  ) => F;
  headerClassName?: string;
  size?: number;
  headerAlign?: 'left' | 'center' | 'right';
}

interface IDataTable {
  /** Classname to place on DataGrid */
  className?: string;
  /** DataGrid columns with additional disableSearch and disableRowSelection property */
  columns: IColDef[];
  /** Callback to set selected rows to be used for deletion, etc. */
  setSelected?: React.Dispatch<React.SetStateAction<GridRowData[]>>;
  /** Specify custom toolbar to show along side table */
  ToolbarComponent?: (props: IToolbarComponent) => ReactNode;
  /** Enabled to cause row selection on cell click */
  enableSelectionOnClick?: boolean;
  updatedRowId?: string;
  sortModel?: GridSortModel;
  setGridSortModel?: (params) => void;
  enableSelectAll?: boolean;
  hideCsv?: boolean;
  hideLastPageButton?: boolean;
  selectedToggle?: boolean;
  setSelectedToggle?: React.Dispatch<React.SetStateAction<boolean>>;
  customPaginationActions?: any;
  hideSortIcon?: boolean;
  hideRowsPerPageOptions?: boolean;
  draggable?: boolean;
  draggable2?: boolean;
  expandable?: boolean;
  onDragEnd?: any;
  onDragMouseDown?: any;
  pageSize?: number;
  onDragMouseUp?: any;
  tabTable?: boolean;
  selected?: GridRowData[];
  setDragSelected?: any;
  openArr?: boolean[];
  filterType?: string;
  openMap?: {
    [key: string]: boolean;
  };
  scrollable?: boolean;
  borderOutline?: boolean;
  checkInitialFilter?: boolean;
  toggleExpandAll?: () => void;
  allOpen?: boolean;
  additionalArraySearchFields?: string[];
  // slideComponent?: React.FC<{ word: string }>;
  // slideComponent?: React.FC<Record<any, any>>;
  slideComponent?: React.ReactNode;
  slideComponentProps?: any;
  setSelectedRowsSearch?: any;
  orderBy?: string;
  order?: 'asc' | 'desc';
  setOrderBy?: (val) => void;
  setOrder?: (val) => void;
  searchText?: string;
  setFilteredRowsForBulkProducts?: (e: any) => void;
  onPaginationPageChange?: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => void;
  // row count to display to use
  displayCount?: number | string;
  tableLayoutFixed?: boolean;
}

/**
 * Sample Styled DataGrid Table
 * @see https://stackoverflow.com/questions/64104554/can-i-initialize-the-checkbox-selection-in-a-material-ui-datagrid
 * @see https://codesandbox.io/s/64104554can-i-initialize-the-checkbox-selection-in-a-material-ui-datagrid-8x0ev?file=/demo.tsx:0-1241
 */

export const DataTable = ({
  className,
  rows,
  columns,
  setSelected,
  ToolbarComponent,
  enableSelectionOnClick,
  updatedRowId,
  sortModel,
  enableSelectAll,
  hideCsv,
  hideLastPageButton,
  customPaginationActions,
  hideSortIcon,
  hideRowsPerPageOptions,
  draggable,
  draggable2,
  expandable,
  onDragEnd,
  onDragMouseDown,
  onDragMouseUp,
  selected,
  setDragSelected,
  scrollable,
  borderOutline,
  tabTable,
  setSelectedRowsSearch,
  onSortModelChange,
  order,
  orderBy,
  setOrderBy,
  sortingOrder,
  setOrder,
  toggleExpandAll,
  checkInitialFilter,
  allOpen,
  additionalArraySearchFields,
  filterType,
  setFilteredRowsForBulkProducts,
  tableLayoutFixed,
  paginationMode,
  selectedToggle,
  setSelectedToggle,
  ...rest
}: IDataTable & DataGridProps) => {
  const [sorting, setSorting] = useState<SortingState>(
    sortModel?.map((model) => ({
      id: model.field,
      desc: model.sort === 'desc',
    })) ?? []
  );

  rest ??= {};
  const classes = useStyles();
  const themeMode = useRecoilValue(themeModeAtom);

  const { initialFilter, initialType } = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const filterParam = urlParams.get('filter');
    const typeParam = urlParams.get('type');
    if (filterParam || typeParam) {
      return {
        initialType: typeParam ?? '',
        initialFilter: filterParam ?? '',
      };
    }
    return {
      initialType: '',
      initialFilter: '',
    };
  }, []);

  // table selection will reset if re-rendering
  const [search, setSearch] = useState<string>(
    checkInitialFilter ? initialFilter : rest?.searchText ?? ''
  );
  // const [fluidWidth, setFluidWidth] = useState<boolean>(false);

  /**
   * Apply search before continuing to have tables properly calculate page size
   * NOTE: must use useMemo so rows do not get updated on re-render
   *
   * Equivalent to the following:
   *  const filteredRows = rows.filter(
   *    (row: any) =>
   *      !search ||
   *      row.queueid.match(new RegExp(search, 'i')) ||
   *      row.queuename.match(new RegExp(search, 'i')) ||
   *      row.email.match(new RegExp(search, 'i')) ||
   *      row.hasGreeting.match(new RegExp(search, 'i')) ||
   *      row.hasRingMusic.match(new RegExp(search, 'i'))
   *  );
   */
  const cleanSearchString = new RegExp(
    search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
    'i'
  );

  const manualPagination = Boolean(rest.onPageChange && rest.onPageSizeChange);

  const filteredRows = React.useMemo(() => {
    if (sorting.length > 0) {
      rows = manualPagination
        ? rows
        : [...rows].sort((a, b) => {
            const aValue = a[sorting[0].id];
            const bValue = b[sorting[0].id];
            if (aValue < bValue) return sorting[0].desc ? 1 : -1;
            if (aValue > bValue) return sorting[0].desc ? -1 : 1;
            return 0;
          });
    }
    return search // only filter if search is populated
      ? rows?.filter(
          (row: any) =>
            columns // check if search found for any column (NOTE: ~(-1) === 0)
              ?.filter((column: IColDef) => !column.disableSearch) // filter out columns that should not be searched
              ?.find((column: IColDef) => {
                const includeField = (
                  additionalArraySearchFields ?? []
                ).includes(column.field);
                const includeAll =
                  includeField &&
                  get(row, `${column.field}[0]`, '')?.includes('All');
                const allType =
                  includeAll &&
                  get(row, `${column.field}[0]`, '')?.split(':')?.[0];
                const includesAdditionalArraySearchFields =
                  includeField &&
                  (get(row, `${column.field}`).some((num) =>
                    cleanSearchString.test(num)
                  ) ||
                    allType === initialType);
                //userid column is number - need to convert to string for proper comparison
                if (column.field === 'userid') {
                  if (get(row, column.field).toString().includes(search)) {
                    return row;
                  }
                }
                return (
                  (get(row, `${column.field}.match`) &&
                    get(row, `${column.field}`).match(cleanSearchString)) ||
                  includesAdditionalArraySearchFields
                );
              }) // find first matching column value
        ) ?? []
      : rows ?? []; // otherwise use current rows
  }, [rows, search, sorting, manualPagination]);

  useEffect(() => {
    const filteredSelectedRows: {
      [key: string]: boolean;
    } = {};
    filteredRows.forEach((filteredRow: any) => {
      filteredSelectedRows[filteredRow.property] = true;
    });
    setSelectedRowsSearch?.(filteredSelectedRows);
  }, [filteredRows]);
  useEffect(() => {
    setFilteredRowsForBulkProducts?.(filteredRows);
  }, [filteredRows]);
  const regex = /(<([^>]+)>)/g;

  useEffect(() => {
    setSearch(rest?.searchText ?? search);
  }, [rest?.searchText]);

  /** Create csvData for CSVLink */
  const csvData = useMemo<Data>(() => {
    const csvHeader = columns.filter((column: any) => !column?.disableExport);
    const csvBody = rows?.map((row) =>
      csvHeader.map((column) =>
        column.valueFormatter
          ? column.valueFormatter({
              row: row,
              value: row[column?.field],
            } as any)
          : row[column.field]
      )
    );
    csvBody?.unshift(csvHeader?.map((column) => column?.headerName));
    return csvBody;
  }, [columns, rows]);

  const htmlRemovedCsvData =
    csvData?.map?.((csv: any) =>
      csv?.map?.((data: any) => {
        return data && typeof data === 'string'
          ? data?.replaceAll(regex, ' ')
          : data;
      })
    ) ?? [];

  const mappedColumns: MRT_ColumnDef<GridRowModel>[] = useMemo(
    () =>
      columns
        .filter((col) => !col.hide)
        .map(
          (col, index) =>
            ({
              header: col.headerName,
              accessorKey: col.field,
              headerClassName: col?.headerClassName,
              Header: col?.Header,
              enableSorting: col?.enableSorting,
              maxSize: col?.width, // add these to the mappedColumns
              size: col?.width, // add these to the mappedColumns
              headerAlign: col?.headerAlign, // add these to the mappedColumns
              align: col?.align, // add these to the mappedColumns
              Cell: (props) => {
                // @todo implement: renderHeader, ,sortComparator, onSortModelChange
                return (
                  col.renderCell?.({
                    id: index,
                    row: props.row.original,
                    value: props.renderedCellValue,
                    api: {} as any,
                    cellMode: 'view',
                    colDef: props.column.columnDef as any,
                    field: props.cell.id,
                    getValue: (id: GridRowId, field: string) =>
                      props.table.getRow(String(id)).getValue(field),
                    hasFocus: false,
                    tabIndex: 0,
                    formattedValue: props.renderedCellValue,
                    rowNode: {} as any,
                  }) ??
                  col.valueFormatter?.({
                    id: index,
                    row: props.row.original,
                    value: props.renderedCellValue,
                    api: {} as any,
                    cellMode: 'view',
                    colDef: props.column.columnDef as any,
                    field: props.cell.id,
                    getValue: (id: GridRowId, field: string) =>
                      props.table.getRow(String(id)).getValue(field),
                    hasFocus: false,
                    tabIndex: 0,
                  }) ??
                  props.renderedCellValue
                );
              },
            } /* satisfies */ as MRT_ColumnDef<GridRowData>)
        ),
    [columns]
  );

  const mappedSortModel: SortingState | undefined = useMemo(
    () =>
      sortModel?.map((model) => ({
        id: model.field,
        desc: model.sort === 'desc',
      })),
    [sortModel]
  );
  const [page, setPage] = React.useState<number>(1);
  const [pageSize2, setPageSize] = React.useState(rest?.pageSize ?? 20);
  const rowsPerPageOptions = rest?.rowsPerPageOptions ?? [10, 20, 50, 100];
  const prevRowCountRef = useRef<number | undefined>(rest?.rowCount);
  const handlePaginationChangePage = (
    event: React.ChangeEvent<any>,
    newPage: number
  ) => {
    if (paginationMode === 'server') {
      setIsSelectAll(false); // boolean that keeps track of whether all rows are selected
      setGlobalSelection({}); // object that keeps track of the globally selected items
      setRowSelection({}); // object that keeps track of the locally selected items
      setSelected?.([]); // array that keeps track of the selected ids sent back to the component
    }
    rest?.onPageChange && rest.onPageChange(newPage, {});
    setPage(newPage);
  };
  const TablePaginationActions = useCallback(
    (props: TablePaginationActionsProps) => (
      <Pagination
        className={props.className}
        count={Math.ceil(props.count / props.rowsPerPage)}
        page={props.page + 1}
        onChange={handlePaginationChangePage}
      />
    ),
    [handlePaginationChangePage]
  );
  const handleChangeRowsPerPage = (event: React.ChangeEvent<any>) => {
    const newPageSize = parseInt(event.target.value, 10);

    // set page-size
    rest?.onPageSizeChange && rest.onPageSizeChange(rest?.page ?? page, {});
    setPageSize(newPageSize);

    // reset page back to 1
    rest?.onPageChange && rest.onPageChange(1, {});
    setPage(1);
  };
  /**
   * Reset page start on search
   *
   * @param value Search string
   */
  const handleSearchChange = useCallback(
    (value: string) => {
      // reset page back to 1
      if (rest.page !== 1 && rest.onPageChange) {
        rest.onPageChange(1, {
          page: 1,
          pageCount: 0,
          pageSize: rest.pageSize,
          rowCount: 0,
          paginationMode: 'server',
        } as any);
      }
      setSearch(value);
    },
    [setSearch, rest]
  );
  /**
   * Dummy function since TablePagination requires handler
   */
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    rest.onPaginationPageChange?.(event, newPage + 1);
    // no-op since property required
  };

  const { treatment: treatmentLoader } = useFeatureFlag(
    'DASH-223-datatable-remove-double-loader'
  );
  const isPreventDoubleLoaderEnabled = useMemo(() => {
    return treatmentLoader === 'control' || treatmentLoader === 'on';
  }, [treatmentLoader]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  // this is the value that is pased back into the table
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [globalSelection, setGlobalSelection] = useState({});
  const [isSelectAll, setIsSelectAll] = useState(false);

  // Prevent state updates during initial mount - use flushSync to ensure synchronous update
  const [isInitialized, setIsInitialized] = useState(false);
  useEffect(() => {
    // Delay initialization to prevent cascading state updates
    const timer = setTimeout(() => {
      setIsInitialized(true);
    }, 0);
    return () => clearTimeout(timer);
  }, []);

  const lastPageChangeTime = useRef(new Date().getTime());
  const pageIndex = (rest?.page ?? 1) - 1; // - 1 to make backwards compatible with @material-ui/data-grid
  const pageSize = rest?.pageSize ?? 20;
  // ENG-7291 - Manual sorting is always true
  // const manualSorting = Boolean(onSortModelChange);

  const handlePageIndexAndSizeChange = (
    updaterOrValue: MRT_Updater<MRT_PaginationState>
  ) => {
    if (paginationMode === 'server') {
      setIsSelectAll(false);
      setGlobalSelection({});
      setRowSelection({});
      setSelected?.([]);
    }
    if (
      typeof updaterOrValue === 'function' &&
      /**
       * updaterOrValue will give pageIndex of 0 during
       * loading causing next page selection to break
       */
      !rest.loading &&
      /**
       * Fallback in case loading not passed since this function
       * is called twice (once for update once for loading) for
       * one page change with pageIndex = 0 when data not in cache.
       *
       * @todo remove timing hack by patching material-react-table package.
       */
      new Date().getTime() - lastPageChangeTime.current > 100
    ) {
      const newPagination = updaterOrValue({
        pageIndex: pageIndex,
        pageSize: pageSize,
      });
      rest.onPageChange?.(newPagination.pageIndex + 1, {}); // @material-ui/data-grid starts index at 1
      if (pageSize !== newPagination.pageSize) {
        rest.onPageSizeChange?.(newPagination.pageSize, {});
      }
      lastPageChangeTime.current = new Date().getTime();
    }
  };

  /** Update caller to notify of selection */
  useEffect(() => {
    if (!isInitialized) return;

    const selectedIds = Object.keys(globalSelection);
    const selectedRows = filteredRows.filter((row) =>
      selectedIds.includes(row.id.toString())
    );
    if (typeof setSelected === 'function') {
      setSelected(selectedRows);
    }
  }, [globalSelection, filteredRows, setSelected, isInitialized]);
  // const dispatch = useDispatch();
  // useEffect(() => {
  //   return () => {
  //     dispatch(updateDisplayLargeTableMsg(false));
  //   };
  // }, [filteredRows]);

  const tableTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: themeMode === themeModeEnum.DARK_MODE ? 'dark' : 'light', //let's use the same dark/light mode as the global theme
          primary: {
            main: '#0098C7',
          }, //swap in the secondary color as the primary for the table
          info: {
            main: 'rgb(255,122,0)', //add in a custom color for the toolbar alert background stuff
          },
          background: {
            default:
              themeMode !== themeModeEnum.DARK_MODE
                ? '#fff' //random light yellow color for the background in light mode
                : '#3a3a3a', //pure black table in dark mode for fun
          },
        },
      }),
    [themeModeEnum, themeMode]
  );
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        const fullScreenButton = document.querySelector(
          'button[aria-label="Toggle full screen"]'
        );
        const tableFullScreen = document.querySelector('.tableFullScreen');
        if (fullScreenButton && tableFullScreen) {
          (fullScreenButton as HTMLElement).click();
        }
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, []);

  useEffect(() => {
    if (sorting.length) {
      const mappedSorting = sorting.map((sort) => {
        return {
          field: sort.id,
          sort: sort.desc ? 'desc' : 'asc',
        };
      });
      onSortModelChange?.(mappedSorting as GridSortModel, {});
    }
  }, [sorting]);
  // paginatedRows only used when manualPagination is false
  const paginatedRows = useMemo(() => {
    const startIndex = (page - 1) * pageSize2;
    const endIndex = startIndex + pageSize2;
    const paginatedData = filteredRows?.slice(startIndex, endIndex);
    return paginatedData;
  }, [filteredRows, page, pageSize2]);

  const rowData = useMemo(
    () => (!manualPagination ? paginatedRows : filteredRows),
    [paginatedRows, filteredRows, manualPagination]
  );

  const CustomPagination = ({
    count,
    page,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
  }) => {
    return (
      <Box display="flex" justifyContent="flex-end" padding={2}>
        <TablePagination
          className={`${classes.pagination} ${
            hideLastPageButton && classes.hideLastPageButton
          }`}
          rowsPerPageOptions={hideRowsPerPageOptions ? [] : rowsPerPageOptions}
          component="div"
          count={count}
          rowsPerPage={rowsPerPage}
          page={page}
          onRowsPerPageChange={onRowsPerPageChange}
          onPageChange={onPageChange}
          ActionsComponent={customPaginationActions || TablePaginationActions}
        />
      </Box>
    );
  };

  const handleSelectAll = () => {
    setIsSelectAll(!isSelectAll);
    if (!isSelectAll) {
      const newGlobalSelection = {};
      filteredRows.forEach((row) => {
        newGlobalSelection[row.id] = true;
      });
      setRowSelection(newGlobalSelection);
      setGlobalSelection(newGlobalSelection);
    } else {
      setGlobalSelection({});
      setRowSelection({});
    }
  };
  const handleRowSelectionChange = (updater) => {
    setRowSelection((prev) => {
      const newSelection =
        typeof updater === 'function' ? updater(prev) : updater;

      let updatedGlobalSelection = { ...globalSelection };

      if (isSelectAll) {
        updatedGlobalSelection = {};
        setIsSelectAll(false);
      }
      Object.keys(newSelection).forEach((key) => {
        updatedGlobalSelection[key] = true;
      });

      Object.keys(globalSelection).forEach((key) => {
        if (!newSelection[key]) delete updatedGlobalSelection[key];
      });
      setGlobalSelection(updatedGlobalSelection);
      return newSelection;
    });
  };
  useEffect(() => {
    if (!isInitialized) return;
    setIsSelectAll(Object.keys(globalSelection).length === filteredRows.length);
  }, [globalSelection, filteredRows, isInitialized]);

  useEffect(() => {
    if (!isInitialized) return;
    if (
      selected?.length === 0 &&
      (Object.keys(globalSelection).length > 0 ||
        Object.keys(rowSelection).length > 0)
    ) {
      setGlobalSelection({});
      setRowSelection({});
      if (typeof setSelected === 'function') {
        setSelected([]);
      }
    }
  }, [selected, selectedToggle, isInitialized]); //DO NOT FIX DEPENDENCYS

  return (
    <Paper className={classes.paper} elevation={0} square={true}>
      {ToolbarComponent?.({
        search,
        setSearch: handleSearchChange,
        csvData: hideCsv ? undefined : htmlRemovedCsvData,
        isLoading: rest.loading,
      })}
      {expandable ? (
        <SlideDataTable
          rows={[...filteredRows]}
          columns={columns}
          openArr={rest?.openMap}
          order={order}
          orderBy={orderBy}
          toggleExpandAll={toggleExpandAll}
          allOpen={allOpen}
          setOrder={setOrder}
          setOrderBy={setOrderBy}
          SlideComponent={rest?.slideComponent}
          slideComponentProps={rest?.slideComponentProps}
          pageSize={rest?.pageSize ?? pageSize2}
          loading={rest?.loading} // hack to associate apiRef
          page={page}
        />
      ) : draggable2 ? (
        <DragDataTable2
          rows={[...filteredRows]}
          columns={columns}
          onDragEnd={onDragEnd}
          onDragMouseDown={onDragMouseDown}
          onDragMouseUp={onDragMouseUp}
          setSelected={setDragSelected}
          selected={selected ?? []}
          enableSelectAll={enableSelectAll ?? false}
          openArr={rest.openArr}
          SlideComponent={rest.slideComponent}
          slideComponentProps={rest.slideComponentProps}
        />
      ) : draggable ? (
        <DragDataTable
          rows={[...filteredRows]}
          columns={columns}
          onDragEnd={onDragEnd}
          setSelected={setDragSelected}
          selected={selected ?? []}
          enableSelectAll={enableSelectAll ?? false}
          pageSize={rest?.pageSize ?? pageSize2}
          page={page}
        />
      ) : !isInitialized ? (
        <TableSkeleton tabTable={tabTable} />
      ) : (
        <ThemeProvider theme={tableTheme}>
          <MaterialReactTable
            columns={mappedColumns}
            data={rowData as GridRowData[]}
            // enableFullScreenToggle={true}
            enableRowSelection={!!setSelected}
            enableGlobalFilter={false} //turn off a feature
            enableRowActions={false}
            enableColumnOrdering={false}
            enableColumnActions={false}
            enableColumnFilters={false}
            enableDensityToggle={false}
            enableHiding={false}
            enableSelectAll={enableSelectAll}
            selectAllMode="all"
            onRowSelectionChange={handleRowSelectionChange}
            rowCount={
              manualPagination ? rest?.rowCount ?? 0 : filteredRows?.length ?? 0
            }
            enablePagination
            getRowId={(originalRow) => originalRow.id}
            manualSorting={true}
            manualPagination={manualPagination} // signifies pagination logic has been applied
            muiTablePaperProps={({ table }) =>
              ({
                elevation: 0,
                className: table.getState().isFullScreen
                  ? 'tableFullScreen'
                  : '',
              } as any)
            }
            muiSelectAllCheckboxProps={{
              checked: isSelectAll,
              indeterminate:
                Object.keys(globalSelection).length > 0 &&
                Object.keys(globalSelection).length < filteredRows.length,
              onChange: handleSelectAll,
            }}
            muiTableHeadCellProps={({ column }) => {
              const colDef = (column.columnDef as unknown) as IColDef;
              const headerClass = colDef?.headerClassName ?? '';
              const align = colDef?.headerAlign ?? 'left';

              return {
                className: `header-align-${align}`,
                sx: {
                  border: headerClass.includes('superStyle')
                    ? '3px solid #ffec61 !important'
                    : undefined,
                  width: colDef?.size || colDef?.width,
                },
              };
            }}
            muiTableBodyCellProps={({ column }) => {
              const colDef = (column.columnDef as unknown) as IColDef;
              const align = colDef?.align ?? 'left';
              return {
                sx: {
                  tableLayout: 'auto', // Ensure the table layout adjusts for content size
                  width: (column?.columnDef as any)?.size,
                  textAlign: align, // Align text based on column definition
                  whiteSpace: 'normal', // Allow text wrapping
                  overflowWrap: 'anywhere', // Break long words if needed
                },
              };
            }}
            {...(tableLayoutFixed
              ? {
                  style: { tableLayout: 'fixed' },
                }
              : {})}
            state={{
              rowSelection: rowSelection,
              isLoading: rest.loading,
              density: 'compact',
              ...(manualPagination
                ? {
                    pagination: {
                      pageIndex: pageIndex,
                      pageSize: pageSize,
                    },
                  }
                : {
                    pagination: {
                      pageIndex: pageIndex,
                      pageSize: pageSize2,
                    },
                  }),
              sorting,
            }}
            paginationDisplayMode="custom"
            renderBottomToolbar={() => {
              const totalCount = manualPagination
                ? rest?.rowCount ?? 0
                : filteredRows?.length ?? 0;
              const currentPage = totalCount > 0
                ? (manualPagination ? pageIndex : page - 1)
                : 0;
              return (
                <CustomPagination
                  count={totalCount}
                  page={currentPage}
                  rowsPerPage={manualPagination ? pageSize : pageSize2}
                  onPageChange={(e) => {
                    handlePageIndexAndSizeChange(e);
                  }}
                  onRowsPerPageChange={(event) => {
                    const newPageSize = parseInt(event.target.value, 10);
                    const update = {
                      pageSize: newPageSize,
                      pageIndex: 0,
                    };
                    if (paginationMode === 'server') {
                      setIsSelectAll(false);
                      setGlobalSelection({});
                      setRowSelection({});
                      if (typeof setSelected === 'function') {
                        setSelected([]);
                      }
                    }
                    handlePageIndexAndSizeChange(() => update);
                    setPageSize(newPageSize);
                    setPage(1);
                  }}
                />
              );
            }}
            positionToolbarAlertBanner="none"
            renderTopToolbarCustomActions={() =>
              Object.keys(globalSelection).length > 0 ||
              Object.keys(rowSelection).length > 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    backgroundColor: 'rgb(255, 244, 234)',
                    width: '100%',
                  }}
                >
                  <div className={classes.tableTopBanner}>
                    <Typography variant="subtitle1">
                      {Object.keys(rowSelection).length} of {rows.length} row(s)
                      selected
                    </Typography>
                    <div>
                      <Button
                        onClick={() => {
                          setIsSelectAll(!isSelectAll);
                          setGlobalSelection({});
                          setRowSelection({});
                          if (typeof setSelected === 'function') {
                            setSelected([]);
                          }
                        }}
                      >
                        Clear Selection
                      </Button>
                    </div>
                  </div>
                </Box>
              ) : (
                <Box></Box>
              )
            }
            onSortingChange={setSorting}
            initialState={{
              pagination: {
                pageIndex: pageIndex,
                pageSize: pageSize,
              },
              sorting: mappedSortModel ?? [],
            }}
          />
        </ThemeProvider>
        // <DataGrid
        //   // onSelectionChange={(newSelection) => {
        //   //   setSelection(newSelection.rows);
        //   // }}
        //   components={assignApiRefComponent}
        //   // {...data}
        //   autoHeight
        //   onCellClick={enableSelectionOnClick ? handleOnRowClick : undefined}
        //   disableSelectionOnClick={true} // override so we can allow button with onClick in cell
        //   disableExtendRowFullWidth={true} // disable add extra column
        //   onSelectionModelChange={handleSelectionChange}
        //   className={clsx(
        //     scrollable && !!filteredRows?.length && classes.scrollable,
        //     className,
        //     borderOutline && classes.borderOutline,
        //     classes.datagrid,
        //     classesWithState.flashAnimation,
        //     classes.datagridFluidHeight,
        //     classes.datagridFluidWidth,
        //     !enableSelectAll && classes.disableSelectAll,
        //     hideSortIcon && classes.hideSortIcon
        //   )}
        //   rows={filteredRows} // rows filtered by search criteria
        //   columns={columns}
        //   checkboxSelection={!!setSelected} // turn on checkbox if setSelected is passed
        //   pageSize={pageSize}
        //   // loading={enableSelectionOnClick} // hack to associate apiRef
        //   rowsPerPageOptions={rowsPerPageOptions}
        //   page={page}
        //   hideFooter={true}
        //   sortingOrder={['desc', 'asc']}
        //   sortModel={sortModel}
        //   {...rest}
        // />
      )}
      {
        rest.loading && !rows?.length && <TableSkeleton tabTable={tabTable} /> // show skeleton only when loading and now rows are empty
      }
      {(expandable || draggable) && (
        <TablePagination
          className={`${classes.pagination} ${
            hideLastPageButton && classes.hideLastPageButton
          }`}
          rowsPerPageOptions={hideRowsPerPageOptions ? [] : rowsPerPageOptions}
          component="div"
          count={
            (rest?.rowCount || prevRowCountRef.current) ??
            filteredRows?.length ??
            0
          }
          rowsPerPage={rest?.pageSize ?? pageSize2}
          page={filteredRows?.length ? (rest?.page ?? page) - 1 : 0} // reset page if data empty
          onRowsPerPageChange={handleChangeRowsPerPage}
          onPageChange={handleChangePage}
          labelRowsPerPage={
            <>
              {rest?.rowCount !== -1 && (
                <span className={classes.paginationTotalRows}>
                  Total Rows:&nbsp;
                  {(
                    rest?.displayCount ??
                    rest?.rowCount ??
                    filteredRows?.length ??
                    0
                  ).toLocaleString()}
                </span>
              )}
              <span>Rows per page</span>
            </>
          }
          ActionsComponent={
            customPaginationActions ||
            (rest?.rowCount === -1 || rest.onPaginationPageChange
              ? undefined
              : TablePaginationActions)
          }
        />
      )}
    </Paper>
  );
};
